import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock3,
  Eye,
  Inbox,
  Mail,
  MessageSquare,
  Package,
  Paperclip,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  Truck,
  Undo2,
  UserRound,
} from 'lucide-react';
import './Support.css';
import PageHeader from '../components/PageHeader';
import { ConfirmModal } from '../ui/kit';
import SupportStartConversation from '../components/SupportStartConversation';
import { datumUndZeit } from '../lib/zeit';

const fmt = (value) => datumUndZeit(value, { jahr: false, leer: '' });

const KIND = {
  ticket: { label: 'Ticket', icon: Mail, tone: 'blue' },
  fundsache: { label: 'Fundsache', icon: Package, tone: 'purple' },
  chat: { label: 'Chat', icon: MessageSquare, tone: 'slate' },
};

const FILTERS = [
  { key: '', label: 'Alle' },
  { key: 'ticket', label: 'Tickets' },
  { key: 'fundsache', label: 'Fundsachen' },
  { key: 'chat', label: 'Chats' },
];

const STATUS_LABEL = {
  neu: 'Neu',
  in_bearbeitung: 'In Bearbeitung',
  geloest: 'Gelöst',
  nicht_geloest: 'Nicht gelöst',
  open: 'Offen',
  closed: 'Geschlossen',
  resolved: 'Erledigt',
  forwarded_to_driver: 'Beim Fahrer',
};

const ROLE_LABEL = {
  customer: 'Kunde',
  driver: 'Fahrer',
  partner: 'Partner',
  admin: 'VOOGO Support',
};

const CATEGORY_LABEL = {
  harassment: 'Belästigung',
  insult: 'Beleidigung',
  threat_or_violence: 'Bedrohung oder Gewalt',
  discrimination: 'Diskriminierung',
  unsafe_ride: 'Unsichere Fahrt',
  bad_experience: 'Schlechte Erfahrung',
  fare_dispute: 'Fahrpreis beanstanden',
  lost_item: 'Fundsache',
  accessibility: 'Barrierefreiheit',
  account_or_app: 'Konto oder App',
  application_or_partnership: 'Bewerbung oder Partnerschaft',
  other: 'Sonstiges',
  support: 'Support (Bestand)',
  anfrage: 'Anfrage (Bestand)',
  fundsache: 'Fundsache (Bestand)',
};

const CASE_SHEET_FIELDS = [
  ['concern', 'Anliegen'],
  ['ride', 'Fahrt'],
  ['route', 'Strecke'],
  ['plate', 'Kennzeichen'],
  ['driver', 'Fahrer'],
  ['customer', 'Kunde'],
  ['intake', 'Aufnahme'],
];

const roleLabel = (value) => ROLE_LABEL[value] || value || 'Nutzer';
const displayCaseValue = (value) => {
  if (typeof value === 'number') return String(value);
  return typeof value === 'string' ? value.trim() : '';
};

function StatusPill({ value }) {
  if (!value) return null;
  const resolved = ['geloest', 'resolved', 'closed'].includes(value);
  const warning = value === 'nicht_geloest';
  return (
    <span className={`support-status ${resolved ? 'is-resolved' : ''} ${warning ? 'is-warning' : ''}`}>
      <i /> {STATUS_LABEL[value] || value}
    </span>
  );
}

function PriorityPill({ priority, category }) {
  if (!priority && !category) return null;
  const urgent = priority === 'urgent';
  return (
    <span className={`support-priority ${urgent ? 'is-urgent' : ''}`}>
      {urgent && <AlertTriangle size={11} />}
      {urgent ? 'Dringend' : 'Normal'}
      {category && <small>{CATEGORY_LABEL[category] || category}</small>}
    </span>
  );
}

function KindPill({ kind }) {
  const definition = KIND[kind] || KIND.chat;
  const Icon = definition.icon;
  return (
    <span className={`support-kind is-${definition.tone}`}>
      <Icon size={11} /> {definition.label}
    </span>
  );
}

// SUPPORT-POSTFACH-AUFRAEUMEN-001 (F5): Der Server liefert die Aufbereitungsfelder NUR, wenn er
// tatsächlich etwas entfernt hat. Fehlen sie, bleibt alles wie bisher — es gibt dann auch keinen
// zusätzlichen Knopf. Das Original wird nie überschrieben, es ist einen Klick entfernt.
const displayView = (text, removed, fassung) => (
  typeof text === 'string' && text ? { text, removed: Array.isArray(removed) ? removed : [], fassung } : undefined
);

function MessageBubble({ outgoing, body, display, meta }) {
  const [zeigeOriginal, setZeigeOriginal] = useState(false);
  const aufbereitet = Boolean(display && typeof display.text === 'string');
  const anzahl = aufbereitet ? display.removed.length : 0;
  return (
    <div className={`support-message ${outgoing ? 'is-outgoing' : ''}`}>
      {/* Ein einziger React-Textknoten — niemals HTML, niemals ein roher HTML-Einschub.
          Der Body ist fremdbestimmt und das Ziel eine Admin-Sitzung mit Geldrechten.
          Der verbotene React-Einschub wird hier bewusst NICHT ausgeschrieben: der
          Quelltext-Wächter aus F9 sucht die Zeichenkette in genau dieser Datei. */}
      <div className="support-message-body">{(!aufbereitet || zeigeOriginal) ? body : display.text}</div>
      {aufbereitet && (
        <div className="support-message-foot">
          <span>
            {zeigeOriginal ? 'Original' : 'Aufbereitet'}
            {anzahl > 0 ? ` · ${anzahl} ${anzahl === 1 ? 'Block' : 'Blöcke'} entfernt` : ''}
          </span>
          {/* Rein clientseitig — kein Request, der Rohtext liegt bereits im Payload. */}
          <button type="button" onClick={() => setZeigeOriginal((wert) => !wert)}>
            {zeigeOriginal ? 'Aufbereitung anzeigen' : 'Original anzeigen'}
          </button>
        </div>
      )}
      <small>{meta}</small>
    </div>
  );
}

// SUPPORT-KI-FALLDATEN-001: Der Entwurf kommt jetzt auch dann, wenn ein Mensch noch etwas
// prüfen oder entscheiden muss — `hint` sagt, was. Nur ganz ohne Entwurf bleibt es bei
// „Eskalation empfohlen". `draftDe` ist die deutsche Prüf-Fassung eines fremdsprachigen
// Entwurfs und wird nie gesendet.
function AiDraft({ draft, label, escalate, hint, draftDe, onAdopt }) {
  if (!draft && !escalate) return null;
  return (
    <section className="support-ai-draft">
      <header>
        <span><Sparkles size={14} /></span>
        <div>
          <strong>Antwortvorschlag</strong>
          <small>KI-Entwurf · {label || 'allgemein'} · wird nie automatisch gesendet</small>
        </div>
        {draft && <button type="button" onClick={onAdopt}>Übernehmen</button>}
      </header>
      {draft
        ? <p>{draft}</p>
        : <p className="is-escalation"><AlertTriangle size={13} /> Eskalation empfohlen — bitte persönlich klären.</p>}
      {draft && draftDe && (
        <p className="support-ai-draft-de"><small>Deutsche Prüf-Fassung (wird nicht gesendet)</small>{draftDe}</p>
      )}
      {draft && hint && (
        <p className="is-escalation"><AlertTriangle size={13} /> Vor dem Senden prüfen: {hint}</p>
      )}
    </section>
  );
}

function DetailLoading() {
  return (
    <div className="support-detail-loading">
      <RefreshCw size={18} />
      <strong>Vorgang wird geladen</strong>
      <small>Der aktuelle Verlauf bleibt vom Sammelpostfach getrennt.</small>
    </div>
  );
}

function DetailLoadError({ onRetry }) {
  return <div role="alert" className="support-detail-loading">
    <strong>Vorgang konnte nicht geladen werden.</strong>
    <button type="button" className="support-button is-secondary" onClick={onRetry}>
      <RefreshCw size={14} /> Erneut laden
    </button>
  </div>;
}

function useSupportDetail(url) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const generation = useRef(0);
  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoadError(false);
    setData(null);
    try {
      const response = await api.get(url);
      if (current === generation.current) setData(response.data);
    } catch {
      if (current === generation.current) setLoadError(true);
    }
  }, [url]);
  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);
  return { data, loadError, load };
}

function SenderIdentity({ userType, user }) {
  const role = roleLabel(userType || user?.role);
  const name = user?.name || 'Name nicht hinterlegt';
  const contact = [user?.email, user?.phone].filter(Boolean).join(' · ');
  return (
    <section className="support-participant" aria-label="Absender">
      <span><UserRound size={16} /></span>
      <div>
        <small>Absender</small>
        <strong>{role}: {name}</strong>
        <p>{contact || 'Kein Kontakt hinterlegt'}</p>
      </div>
    </section>
  );
}

function CaseSheet({ rows }) {
  if (!rows.length) return null;
  return (
    <section className="support-case-sheet" aria-label="Fallblatt">
      <header>
        <span className="support-overline">Fallblatt</span>
        <strong>Aufgenommene Angaben</strong>
      </header>
      <dl>
        {rows.map(([key, label, value]) => (
          <div key={key}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// Stable seam for the current case sheet and later independently gated detail cards.
function DetailExtensionArea({ caseSheet, showCaseSheet = false, children }) {
  const rows = CASE_SHEET_FIELDS
    .map(([key, label]) => [key, label, displayCaseValue(caseSheet?.[key])]);
  if (!showCaseSheet && !children) return null;
  return (
    <div className="support-detail-extensions">
      {showCaseSheet && <CaseSheet rows={rows} />}
      {children}
    </div>
  );
}

function ReplyChannelChoice({ value, onChange, emailAvailable, chatAvailable = true }) {
  return (
    <fieldset className="support-reply-channels">
      <legend>Antwortkanal</legend>
      <label className={`${value === 'chat' ? 'is-selected' : ''} ${chatAvailable ? '' : 'is-disabled'}`}>
        <input
          type="radio"
          name="support-reply-channel"
          value="chat"
          checked={value === 'chat'}
          disabled={!chatAvailable}
          onChange={() => onChange('chat')}
        />
        <MessageSquare size={14} />
        <span><strong>Chat</strong><small>{chatAvailable ? 'Direkt im VOOGO-Posteingang' : 'Kein verknüpftes App-Konto'}</small></span>
      </label>
      <label className={`${value === 'email' ? 'is-selected' : ''} ${emailAvailable ? '' : 'is-disabled'}`}>
        <input
          type="radio"
          name="support-reply-channel"
          value="email"
          checked={value === 'email'}
          disabled={!emailAvailable}
          onChange={() => onChange('email')}
        />
        <Mail size={14} />
        <span><strong>E-Mail</strong><small>{emailAvailable ? 'Per E-Mail zustellen' : 'Für diesen Absender nicht verfügbar'}</small></span>
      </label>
    </fieldset>
  );
}

const attachmentSize = (bytes) => {
  const value = Number(bytes || 0);
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.ceil(value / 1024))} KB`;
};

const attachmentRequestLabel = (status) => ({
  active: 'Aktiv',
  expired: 'Abgelaufen',
  limit_reached: '5/5 erreicht',
}[status] || status);

const safeAttachmentRouteId = (value) => typeof value === 'string'
  && value.length > 0 && value.length <= 200
  && /^[A-Za-z0-9][A-Za-z0-9._~-]*$/.test(value)
  && value !== '.' && value !== '..';

function validAttachmentAdminState(data, conversationId) {
  if (!data || data.conversation_id !== conversationId
    || !Array.isArray(data.attachment_requests)
    || !Array.isArray(data.attachments)
    || !Array.isArray(data.access_audit)) return false;
  const requests = data.attachment_requests;
  if (!requests.every((request) => request?.conversation_id === conversationId
    && safeAttachmentRouteId(request.id))) return false;
  const requestIds = new Set(requests.map((request) => request.id));
  if (data.active_attachment_request != null
    && (data.active_attachment_request.conversation_id !== conversationId
      || !safeAttachmentRouteId(data.active_attachment_request.id)
      || !requestIds.has(data.active_attachment_request.id))) return false;
  if (!data.attachments.every((attachment) => attachment?.conversation_id === conversationId
    && safeAttachmentRouteId(attachment.id)
    && safeAttachmentRouteId(attachment.request_id)
    && requestIds.has(attachment.request_id))) return false;
  const attachmentIds = new Set(data.attachments.map((attachment) => attachment.id));
  return data.access_audit.every((entry) => safeAttachmentRouteId(entry?.attachment_id)
    && attachmentIds.has(entry.attachment_id));
}

function SupportAttachmentsPanel({ conversationId, disabled = false }) {
  const [snapshot, setSnapshot] = useState({ conversationId: null, data: null, error: '' });
  const [busy, setBusy] = useState(false);
  const loadGeneration = useRef(0);
  const currentConversation = useRef(conversationId);
  // Ref schon während des Renders umstellen: ein alter Promise aus A darf auch
  // im kurzen Fenster vor dem B-useEffect keinerlei Aktion mehr ausführen.
  currentConversation.current = conversationId;
  const actionIsCurrent = (generation, expectedConversation) => generation === loadGeneration.current
    && currentConversation.current === expectedConversation;

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setSnapshot({ conversationId, data: null, error: '' });
    try {
      const response = await api.get(`/admin/support-inbox/conversations/${conversationId}/attachments`);
      if (!validAttachmentAdminState(response.data, conversationId)) {
        throw new Error('contradictory attachment state');
      }
      if (generation === loadGeneration.current) {
        setSnapshot({ conversationId, data: response.data, error: '' });
      }
    } catch {
      if (generation === loadGeneration.current) {
        setSnapshot({ conversationId, data: null, error: 'Anhangdaten nicht verfügbar.' });
      }
    }
  }, [conversationId]);

  useEffect(() => {
    setBusy(false);
    load();
    return () => { loadGeneration.current += 1; };
  }, [load]);

  const requestAttachments = async () => {
    const actionGeneration = loadGeneration.current;
    const actionConversation = conversationId;
    setBusy(true);
    try {
      const response = await api.post(`/admin/support-inbox/conversations/${actionConversation}/attachment-requests`);
      if (!actionIsCurrent(actionGeneration, actionConversation)) return;
      toast.success(response.status === 200 ? 'Anhangfreigabe ist bereits aktiv' : 'Anhänge für 72 Stunden angefordert');
      setBusy(false);
      await load();
    } catch (requestError) {
      if (actionIsCurrent(actionGeneration, actionConversation)) {
        toast.error(requestError.response?.data?.message || 'Anhänge konnten nicht angefordert werden');
      }
    } finally {
      if (actionIsCurrent(actionGeneration, actionConversation)) setBusy(false);
    }
  };

  const openAttachment = async (attachment) => {
    const actionGeneration = loadGeneration.current;
    const actionConversation = conversationId;
    try {
      const response = await api.get(
        `/admin/support-inbox/conversations/${actionConversation}/attachments/${attachment.id}/content`,
        { responseType: 'blob' },
      );
      if (!actionIsCurrent(actionGeneration, actionConversation)) return;
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: attachment.mime_type });
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
      await load(); // der erfolgreiche private Abruf ist jetzt im Audit sichtbar
    } catch (openError) {
      if (actionIsCurrent(actionGeneration, actionConversation)) {
        toast.error(openError.response?.data?.message || 'Anhang konnte nicht privat geöffnet werden');
      }
    }
  };

  // Ein alter Promise darf niemals Metadaten/Audit aus Gespräch A unter B
  // rendern. Schon der synchrone Prop-Render blendet deshalb fremde Snapshots aus.
  const state = snapshot.conversationId === conversationId ? snapshot.data : null;
  const error = snapshot.conversationId === conversationId ? snapshot.error : '';
  const active = state?.active_attachment_request;
  return (
    <section className="support-attachments" aria-label="Support-Anhänge">
      <header>
        <span><Paperclip size={15} /></span>
        <div>
          <strong>Angeforderte Anhänge</strong>
          <small>Bilder/Videos · privat · jeder Abruf protokolliert</small>
        </div>
        <button
          type="button"
          className="support-button is-secondary"
          onClick={requestAttachments}
          disabled={disabled || busy || Boolean(active)}
        >
          <Paperclip size={12} /> {busy ? 'Fordere an…' : active ? 'Freigabe aktiv' : 'Anhänge anfordern'}
        </button>
      </header>

      {error && <p className="support-attachment-error">{error}</p>}
      {active && (
        <div className="support-attachment-active">
          <strong>Bis {fmt(active.expires_at)} freigeschaltet</strong>
          <small>{active.uploaded_count}/{active.max_attachments} hochgeladen · {active.remaining_uploads} offen</small>
        </div>
      )}

      {(state?.attachments || []).length > 0 && (
        <div className="support-attachment-files">
          {(state.attachments || []).map((attachment) => (
            <article key={attachment.id}>
              <span className="support-attachment-kind">{attachment.media_kind === 'video' ? 'VIDEO' : 'BILD'}</span>
              <div>
                <strong>{attachment.name}</strong>
                <small>
                  {attachmentSize(attachment.size_bytes)} · {fmt(attachment.uploaded_at)} ·
                  {' '}{roleLabel(attachment.uploaded_by?.role)}
                </small>
              </div>
              <button type="button" onClick={() => openAttachment(attachment)}>
                <Eye size={12} /> Privat öffnen
              </button>
            </article>
          ))}
        </div>
      )}

      {(state?.attachment_requests || []).length > 0 && (
        <details className="support-attachment-history">
          <summary>Aufforderungen ({state.attachment_requests.length})</summary>
          {(state.attachment_requests || []).map((request) => (
            <div key={request.id}>
              <span>{attachmentRequestLabel(request.status)}</span>
              <strong>{request.requested_by_admin?.name || 'Admin'}</strong>
              <small>{fmt(request.requested_at)} · bis {fmt(request.expires_at)} · {request.uploaded_count}/{request.max_attachments}</small>
            </div>
          ))}
        </details>
      )}

      {(state?.access_audit || []).length > 0 && (
        <details className="support-attachment-history is-audit">
          <summary><ShieldCheck size={12} /> Private Abrufe ({state.access_audit.length})</summary>
          {(state.access_audit || []).map((entry) => (
            <div key={entry.id}>
              <span>{roleLabel(entry.actor_role)}</span>
              <strong>{entry.actor_user_id}</strong>
              <small>{fmt(entry.accessed_at)} · Anhang {entry.attachment_id}</small>
            </div>
          ))}
        </details>
      )}
    </section>
  );
}

function TicketDetail({ id, archived, onChanged, onArchive }) {
  const { data, loadError, load } = useSupportDetail(`/admin/support/tickets/${id}`);
  const [reply, setReply] = useState('');
  // Kanalwahl: Standard ist CHAT, sobald ein verknüpftes App-Konto da ist —
  // E-Mail geht nur noch auf ausdrückliche Wahl raus (SUPPORT-IDENTITAET-UND-KI-001).
  const [channel, setChannel] = useState('chat');
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    setReply('');
    setChannel('chat');
  }, [load]);
  useEffect(() => {
    if (data) setChannel(data.delivery_channels?.chat ? 'chat' : 'email');
  }, [data]);

  if (loadError) return <DetailLoadError onRetry={load} />;
  if (!data) return <DetailLoading />;
  const ticket = data.ticket;
  const chatAvailable = data.delivery_channels?.chat === true;
  const emailAvailable = data.delivery_channels?.email === true;

  const setStatus = async (status, done) => {
    try {
      await api.patch(`/admin/support/tickets/${ticket.id}`, { status });
      toast.success(done ? 'Ins Archiv verschoben' : 'Status aktualisiert');
      if (done) onArchive?.();
      else {
        load();
        onChanged?.();
      }
    } catch {
      toast.error('Fehler');
    }
  };

  const reopen = async () => {
    try {
      await api.post(`/admin/support-inbox/reopen/ticket/${ticket.id}`);
      toast.success('Wieder geöffnet');
      onArchive?.();
    } catch {
      toast.error('Fehler');
    }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${ticket.id}/reply`, { body: reply, channel });
      toast.success(`Antwort per ${channel === 'email' ? 'E-Mail' : 'Chat'} gesendet`);
      setReply('');
      load();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Senden fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const draft = async () => {
    setAiBusy(true);
    try {
      const response = await api.post(`/admin/assistant/ticket-draft/${ticket.id}`);
      if (response.data.escalate) {
        toast(response.data.draft ? 'Entwurf erstellt — vor dem Senden prüfen' : 'Eskalation empfohlen', { icon: '⚠️' });
      }
      else toast.success('Entwurf erstellt');
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'KI-Entwurf nicht verfügbar');
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="support-detail">
      <header className="support-detail-head">
        <div className="support-detail-title">
          <span className="support-detail-icon"><Mail size={17} /></span>
          <div>
            <span className="support-overline">E-Mail-Ticket</span>
            <h2>{ticket.subject}</h2>
            <p>{ticket.from_name ? `${ticket.from_name} · ` : ''}{ticket.from_email}</p>
          </div>
        </div>
        <StatusPill value={ticket.status} />
        <PriorityPill priority={data.case_header?.priority || ticket.priority} category={data.case_header?.category || ticket.category} />
        <div className="support-actions">
          {archived ? (
            <button type="button" className="support-button is-secondary" onClick={reopen}>
              <RefreshCw size={13} /> Wieder öffnen
            </button>
          ) : (
            <>
              <button type="button" className="support-button is-secondary" onClick={() => setStatus('in_bearbeitung')}>
                <Clock3 size={13} /> In Bearbeitung
              </button>
              <button type="button" className="support-button is-resolve" onClick={() => setStatus('geloest', true)}>
                <CheckCircle2 size={13} /> Gelöst
              </button>
            </>
          )}
          <button type="button" className="support-button is-ai" onClick={draft} disabled={aiBusy}>
            <Sparkles size={13} /> {aiBusy ? 'Erstelle…' : 'KI-Entwurf'}
          </button>
        </div>
      </header>

      <SenderIdentity
        userType={data.linked_account?.user_type}
        user={data.linked_account || { name: 'Kein verknüpftes Konto (Legacy/extern)', email: ticket.from_email }}
      />

      <section className="support-thread" aria-label="Ticket-Verlauf">
        {(data.messages || []).map((message) => (
          <MessageBubble
            key={message.id}
            outgoing={message.direction === 'out'}
            body={message.body}
            display={displayView(message.body_display, message.body_removed, message.body_display_fassung)}
            meta={`${message.direction === 'out' ? 'VOOGO Support' : (message.from_email || '')} · ${fmt(message.created_at)}`}
          />
        ))}
      </section>

      <AiDraft
        draft={ticket.ai_draft}
        label={ticket.ai_label}
        escalate={!ticket.ai_draft && Boolean(ticket.ai_label)}
        hint={ticket.ai_hint}
        draftDe={ticket.ai_lang && ticket.ai_lang !== 'de' ? ticket.ai_draft_de : null}
        onAdopt={() => setReply(ticket.ai_draft)}
      />

      <footer className="support-composer">
        <ReplyChannelChoice
          value={channel}
          onChange={setChannel}
          emailAvailable={emailAvailable}
          chatAvailable={chatAvailable}
        />
        <label htmlFor={`support-ticket-reply-${ticket.id}`}>Antwort an den Absender</label>
        <textarea
          id={`support-ticket-reply-${ticket.id}`}
          value={reply}
          onChange={(event) => setReply(event.target.value)}
          placeholder="Antwort an den Absender…"
          rows={3}
        />
        <div>
          <small>Versand erfolgt erst nach manueller Freigabe.</small>
          <button type="button" className="support-send" onClick={send} disabled={busy || !reply.trim() || (channel === 'chat' ? !chatAvailable : !emailAvailable)}>
            <Send size={13} /> {busy ? 'Senden…' : `${channel === 'email' ? 'E-Mail' : 'Chat'} senden`}
          </button>
        </div>
      </footer>
    </div>
  );
}

function FundsacheDetail({ id, archived, onChanged, onArchive }) {
  const [item, setItem] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [note, setNote] = useState('');
  const [reply, setReply] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api.get('/admin/lost-items');
      const current = (response.data.items || []).find((entry) => entry.id === id) || null;
      setItem(current);
      if (current?.case_conversation_id) {
        const detail = await api.get(`/admin/conversations/${current.case_conversation_id}/messages`);
        setCaseData(detail.data);
      } else {
        setCaseData(null);
      }
    } catch { /* keep current detail while the next refresh retries */ }
  }, [id]);

  useEffect(() => {
    setItem(null);
    setCaseData(null);
    setNote('');
    setReply('');
    setAi(null);
    load();
  }, [load]);

  if (!item) return <DetailLoading />;

  const act = async (action) => {
    try {
      await api.patch(`/admin/lost-items/${item.id}`, { action, admin_note: note || undefined });
      toast.success(action === 'resolve' ? 'Ins Archiv verschoben' : 'An Fahrer weitergeleitet');
      if (action === 'resolve') onArchive?.();
      else {
        load();
        onChanged?.();
      }
    } catch {
      toast.error('Fehler');
    }
  };

  const reopen = async () => {
    try {
      await api.post(`/admin/support-inbox/reopen/fundsache/${item.id}`);
      toast.success('Wieder geöffnet');
      onArchive?.();
    } catch {
      toast.error('Fehler');
    }
  };

  const draft = async () => {
    setAiBusy(true);
    try {
      const response = await api.post(`/admin/support-inbox/fundsache-draft/${item.id}`);
      setAi(response.data);
      if (response.data.escalate) {
        toast(response.data.draft ? 'Entwurf erstellt — vor dem Senden prüfen' : 'Eskalation empfohlen', { icon: '⚠️' });
      }
      else toast.success('Entwurf erstellt');
    } catch (error) {
      toast.error(error.response?.data?.error || 'KI-Entwurf nicht verfügbar');
    } finally {
      setAiBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !item.case_conversation_id) return;
    setReplyBusy(true);
    try {
      await api.post(`/admin/conversations/${item.case_conversation_id}/message`, {
        message: reply,
        channel: 'chat',
      });
      toast.success('Antwort im Fundsachen-Gespräch gesendet');
      setReply('');
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Senden fehlgeschlagen');
    } finally {
      setReplyBusy(false);
    }
  };

  return (
    <div className="support-detail">
      <header className="support-detail-head">
        <div className="support-detail-title">
          <span className="support-detail-icon"><Package size={17} /></span>
          <div>
            <span className="support-overline">Fundsache</span>
            <h2>{item.customer_name || 'Unbekannter Kunde'}</h2>
            <p>{item.customer_phone || item.customer_email || 'Kein Kontakt hinterlegt'}</p>
          </div>
        </div>
        <StatusPill value={item.status} />
        <PriorityPill priority={item.case_priority || 'normal'} category={item.case_category || 'lost_item'} />
      </header>

      <div className="support-lost-content">
        <section className="support-fact-card">
          <span className="support-overline">Gemeldeter Gegenstand</span>
          <p>{item.description}</p>
        </section>
        <div className="support-fact-grid">
          <section>
            <span>Fahrt</span>
            <strong>{item.pickup_address || '—'} → {item.dropoff_address || '—'}</strong>
            <small>{item.ride_date ? fmt(item.ride_date) : 'Fahrtdatum nicht hinterlegt'}</small>
          </section>
          <section>
            <span>Fahrer</span>
            <strong>{item.driver_name || 'Nicht zugeordnet'}</strong>
            <small>{item.driver_phone || 'Keine Telefonnummer'}{item.driver_response ? ` · „${item.driver_response}“` : ''}</small>
          </section>
        </div>

        {item.case_conversation_id && (
          <DetailExtensionArea>
            <SupportAttachmentsPanel
              conversationId={item.case_conversation_id}
              disabled={archived}
            />
          </DetailExtensionArea>
        )}

        <AiDraft
          draft={ai?.draft}
          escalate={ai?.escalate}
          hint={ai?.pruefhinweis}
          draftDe={ai?.language && ai.language !== 'de' ? ai?.draft_de : null}
          onAdopt={() => setNote(ai.draft)}
        />

        {caseData && (
          <section className="support-lost-thread" aria-label="Fundsachen-Gespräch">
            <span className="support-overline">Gespräch mit dem Kunden</span>
            <div className="support-thread">
              {(caseData.messages || []).map((message) => (
                <MessageBubble
                  key={message.id}
                  outgoing={message.sender_role === 'admin'}
                  body={message.message}
                  display={displayView(message.message_display, message.message_removed, message.message_display_fassung)}
                  meta={`${message.sender_role === 'admin' ? 'VOOGO Support' : item.customer_name || 'Kunde'} · ${fmt(message.created_at)}`}
                />
              ))}
            </div>
            <div className="support-lost-reply">
              <label htmlFor={`support-lost-reply-${item.id}`}>Antwort an den Kunden</label>
              <textarea
                id={`support-lost-reply-${item.id}`}
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                placeholder="Nachricht schreiben…"
                rows={3}
              />
              <button type="button" className="support-send" onClick={sendReply} disabled={replyBusy || !reply.trim()}>
                <Send size={13} /> {replyBusy ? 'Senden…' : 'Chat senden'}
              </button>
            </div>
          </section>
        )}

        <section className="support-note">
          <label htmlFor={`support-lost-note-${item.id}`}>Notiz für Fahrer oder Kunde</label>
          <textarea
            id={`support-lost-note-${item.id}`}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Notiz für Fahrer/Kunde (optional)…"
            rows={3}
          />
        </section>

        <div className="support-actions is-lost">
          <button type="button" className="support-button is-ai" onClick={draft} disabled={aiBusy}>
            <Sparkles size={13} /> {aiBusy ? 'Erstelle…' : 'KI-Entwurf'}
          </button>
          {archived ? (
            <button type="button" className="support-button is-secondary" onClick={reopen}>
              <RefreshCw size={13} /> Wieder öffnen
            </button>
          ) : (
            <>
              {item.driver_id && (
                <button type="button" className="support-button is-secondary" onClick={() => act('forward_to_driver')}>
                  <Truck size={13} /> An Fahrer weiterleiten
                </button>
              )}
              <button type="button" className="support-button is-resolve" onClick={() => act('resolve')}>
                <CheckCircle2 size={13} /> Erledigt
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatDetail({ id, archived, onChanged, onArchive }) {
  const { data, loadError, load } = useSupportDetail(`/admin/conversations/${id}/messages`);
  const [reply, setReply] = useState('');
  const [channel, setChannel] = useState('chat');
  const [busy, setBusy] = useState(false);
  const [counterBusy, setCounterBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    setReply('');
    setChannel('chat');
    setCounterBusy(false);
    setAi(null);
  }, [load]);

  if (loadError) return <DetailLoadError onRetry={load} />;
  if (!data) return <DetailLoading />;
  const emailAvailable = data.delivery_channels?.email === true;
  const participantRole = roleLabel(data.conversation?.user_type || data.user?.role);
  const participantName = data.user?.name || 'Nutzer';

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/conversations/${id}/message`, { message: reply, channel });
      toast.success(`Antwort per ${channel === 'email' ? 'E-Mail' : 'Chat'} gesendet`);
      setReply('');
      load();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Senden fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    try {
      await api.post(`/admin/support-inbox/chat/${id}/close`);
      toast.success('Ins Archiv verschoben');
      onArchive?.();
    } catch {
      toast.error('Fehler');
    }
  };

  const reopen = async () => {
    try {
      await api.post(`/admin/support-inbox/reopen/chat/${id}`);
      toast.success('Wieder geöffnet');
      onArchive?.();
    } catch {
      toast.error('Fehler');
    }
  };

  const draft = async () => {
    setAiBusy(true);
    try {
      const response = await api.post(`/admin/support-inbox/chat-draft/${id}`);
      setAi(response.data);
      if (response.data.escalate) {
        toast(response.data.draft ? 'Entwurf erstellt — vor dem Senden prüfen' : 'Eskalation empfohlen', { icon: '⚠️' });
      }
      else toast.success('Entwurf erstellt');
    } catch (error) {
      toast.error(error.response?.data?.error || 'KI-Entwurf nicht verfügbar');
    } finally {
      setAiBusy(false);
    }
  };

  const sendCounterstatement = async () => {
    const caseId = data.conversation?.source_ticket_id;
    if (!caseId || !data.case_link?.can_send_counterstatement) return;
    setCounterBusy(true);
    try {
      await api.post(`/admin/support-inbox/cases/${caseId}/counterstatement`);
      toast.success('Separater Bericht wurde manuell an den Beschuldigten gesendet');
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Bericht konnte nicht gesendet werden');
    } finally {
      setCounterBusy(false);
    }
  };

  return (
    <div className="support-detail">
      <header className="support-detail-head">
        <div className="support-detail-title">
          <span className="support-detail-icon"><MessageSquare size={17} /></span>
          <div>
            <span className="support-overline">Support-Gespräch</span>
            <h2>{participantName}</h2>
            <p>{participantRole} · {data.conversation?.status}</p>
          </div>
        </div>
        <StatusPill value={data.conversation?.status} />
        {data.conversation?.kind === 'case' && (
          <PriorityPill priority={data.case_header?.priority} category={data.case_header?.category} />
        )}
        <div className="support-actions">
          <button type="button" className="support-button is-ai" onClick={draft} disabled={aiBusy}>
            <Sparkles size={13} /> {aiBusy ? 'Erstelle…' : 'KI-Entwurf'}
          </button>
          {data.case_link?.can_send_counterstatement && data.conversation?.source_ticket_id && (
            <button type="button" className="support-button is-urgent" onClick={sendCounterstatement} disabled={counterBusy}>
              <AlertTriangle size={13} /> {counterBusy ? 'Wird gesendet…' : 'Bericht an Beschuldigten senden'}
            </button>
          )}
          {archived ? (
            <button type="button" className="support-button is-secondary" onClick={reopen}>
              <RefreshCw size={13} /> Wieder öffnen
            </button>
          ) : (
            <button type="button" className="support-button is-resolve" onClick={close}>
              <CheckCircle2 size={13} /> Gelöst
            </button>
          )}
        </div>
      </header>

      <SenderIdentity userType={data.conversation?.user_type} user={data.user} />

      <DetailExtensionArea
        caseSheet={data.case_sheet}
        showCaseSheet={data.conversation?.kind === 'case'}
      >
        {(data.case_link?.linked_case_id || data.case_link?.counterstatement_sent_at) && (
          <section className="support-case-link" aria-label="Admin-Fallverknüpfung">
            <strong>Gegendarstellung getrennt angelegt</strong>
            <small>
              Nur Admin sichtbar · Fall {data.case_link.linked_case_id || '—'}
              {data.case_link.counterstatement_sent_at ? ` · ${fmt(data.case_link.counterstatement_sent_at)}` : ''}
            </small>
          </section>
        )}
        <SupportAttachmentsPanel conversationId={id} disabled={archived} />
      </DetailExtensionArea>

      <section className="support-thread" aria-label="Chat-Verlauf">
        {(data.messages || []).map((message) => (
          <MessageBubble
            key={message.id}
            outgoing={message.sender_role === 'admin'}
            body={message.message}
            display={displayView(message.message_display, message.message_removed, message.message_display_fassung)}
            meta={`${message.sender_role === 'admin' ? 'VOOGO Support' : `${participantRole}: ${participantName}`} · ${fmt(message.created_at)}`}
          />
        ))}
      </section>

      <AiDraft
        draft={ai?.draft}
        escalate={ai?.escalate}
        hint={ai?.pruefhinweis}
        draftDe={ai?.language && ai.language !== 'de' ? ai?.draft_de : null}
        onAdopt={() => setReply(ai.draft)}
      />

      <footer className="support-composer">
        <ReplyChannelChoice
          value={channel}
          onChange={setChannel}
          emailAvailable={emailAvailable}
        />
        <label htmlFor={`support-chat-reply-${id}`}>Antwort an {participantName}</label>
        <textarea
          id={`support-chat-reply-${id}`}
          value={reply}
          onChange={(event) => setReply(event.target.value)}
          placeholder="Nachricht schreiben…"
          rows={3}
        />
        <div>
          <small>Unabhängig vom gewählten Kanal wird die Antwort im Gespräch protokolliert.</small>
          <button type="button" className="support-send" onClick={send} disabled={busy || !reply.trim()}>
            <Send size={13} /> {busy ? 'Senden…' : `${channel === 'email' ? 'E-Mail' : 'Chat'} senden`}
          </button>
        </div>
      </footer>
    </div>
  );
}

// ── SUPPORT-POSTFACH-AUFRAEUMEN-001 (F3) ────────────────────────────────────────────
// Der Server benennt jede Ablehnung; hier stehen nur die deutschen Namen dazu. Ein
// unbekannter Grund wird wörtlich durchgereicht statt verschluckt.
const BULK_REASON = {
  already_archived: 'bereits archiviert',
  already_active: 'bereits aktiv',
  not_found: 'nicht gefunden',
  unknown_kind: 'unbekannte Vorgangsart',
  counterstatement_pair: 'Gegendarstellung nur halb ausgewählt',
  not_archived: 'noch nicht archiviert',
  not_hidden: 'nicht entfernt',
  already_hidden: 'bereits entfernt',
  already_visible: 'bereits sichtbar',
};

// Zwei Routen, vier Aktionen. `entfernen`/`wiederherstellen` fassen ausschließlich
// hidden_admin an — es wird nie eine Zeile gelöscht, nur aus dem Postfach ausgeblendet.
const BULK_REMOVE_ACTIONS = new Set(['entfernen', 'wiederherstellen']);
const bulkRoute = (action) => (BULK_REMOVE_ACTIONS.has(action)
  ? '/admin/support-inbox/bulk-remove'
  : '/admin/support-inbox/bulk');

const BULK_TITEL = {
  archivieren: 'Ausgewählte archivieren?',
  wieder_oeffnen: 'Ausgewählte zurückholen?',
  wiederherstellen: 'Ausgewählte zurückholen?',
  entfernen: 'Ausgewählte entfernen?',
};

const zerlegeSchluessel = (key) => {
  const trenner = key.indexOf(':');
  return { kind: key.slice(0, trenner), id: key.slice(trenner + 1) };
};

export default function Support() {
  const [startingConversation, setStartingConversation] = useState(false);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('aktiv');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // F1: Auswahl (kind:id), Anker für Shift-Klick und Kappungshinweis.
  const [picked, setPicked] = useState(() => new Set());
  const anchorRef = useRef(null);
  // Die Umschalttaste wird beim KLICK gemerkt, nicht beim change-Ereignis: React synthetisiert
  // change für Checkboxen aus demselben Klick, aber die Modifiertaste ist dort nicht verlässlich
  // vorhanden — gemessen, nicht vermutet. onClick läuft in derselben Auslieferung vor onChange.
  const shiftRef = useRef(false);
  const [truncated, setTruncated] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rueckfrage, setRueckfrage] = useState(null);
  const [bulkNote, setBulkNote] = useState(null);
  const listGeneration = useRef(0);

  const load = useCallback(() => {
    const generation = ++listGeneration.current;
    api.get('/admin/support-inbox', { params: { view } })
      .then((response) => {
        if (generation !== listGeneration.current) return;
        const frisch = response.data.items || [];
        setItems(frisch);
        setCounts(response.data.counts || {});
        setTruncated(Boolean(response.data.truncated));
        // Der 30-Sekunden-Takt ersetzt `items` komplett. Was der Server nicht mehr liefert,
        // darf nicht als Geist im nächsten Stapel mitfahren.
        const vorhanden = new Set(frisch.map((item) => `${item.kind}:${item.id}`));
        setPicked((vorher) => {
          if (vorher.size === 0) return vorher;
          const uebrig = new Set([...vorher].filter((key) => vorhanden.has(key)));
          return uebrig.size === vorher.size ? vorher : uebrig;
        });
        setError('');
      })
      .catch(() => { if (generation === listGeneration.current) setError('Support-Posteingang konnte nicht aktualisiert werden.'); })
      .finally(() => { if (generation === listGeneration.current) setLoading(false); });
  }, [view]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => { clearInterval(timer); listGeneration.current += 1; };
  }, [load]);

  const afterArchive = useCallback(() => {
    setSelected(null);
    setPicked(new Set());
    anchorRef.current = null;
    load();
  }, [load]);

  const normalizedQuery = query.trim().toLowerCase();
  const shown = items.filter((item) => (
    (!filter || item.kind === filter)
    && (!normalizedQuery || `${item.title || ''} ${item.who || ''} ${item.snippet || ''}`.toLowerCase().includes(normalizedQuery))
  ));

  const shownKeys = shown.map((item) => `${item.kind}:${item.id}`);
  const pickedItems = items.filter((item) => picked.has(`${item.kind}:${item.id}`));
  const versteckteAuswahl = pickedItems.filter((item) => !shownKeys.includes(`${item.kind}:${item.id}`)).length;
  const alleSichtbarGewaehlt = shownKeys.length > 0 && shownKeys.every((key) => picked.has(key));
  // Knöpfe schalten sich ausschließlich nach den Serverangaben frei — nie nach eigener Vermutung.
  const bietetAn = (name) => pickedItems.some((item) => Boolean(item.actions && item.actions[name]));

  // Eine Markierung ist reine Anzeige: sie löst KEINEN Request aus.
  const togglePick = (item, shiftKey) => {
    const key = `${item.kind}:${item.id}`;
    // Anker und Reihenfolge werden HIER gelesen, nicht im Aktualisierer. React führt die
    // Funktion in setPicked(fn) erst in der Renderphase aus — also nach `anchorRef.current = key`
    // weiter unten. Ein Lesen von anchorRef INNERHALB von fn liefert deshalb immer den eigenen
    // Schlüssel, `anker !== key` ist nie wahr und der Shift-Bereich wird still übersprungen.
    // Gemessen, nicht vermutet: genau daran scheiterte der Bereichsklick.
    const anker = anchorRef.current;
    const reihenfolge = shownKeys;
    setPicked((vorher) => {
      const naechste = new Set(vorher);
      // Shift-Klick markiert den Bereich der GEZEIGTEN (gefilterten) Liste, nichts dahinter.
      if (shiftKey && anker && anker !== key) {
        const von = reihenfolge.indexOf(anker);
        const bis = reihenfolge.indexOf(key);
        if (von >= 0 && bis >= 0) {
          const [a, b] = von <= bis ? [von, bis] : [bis, von];
          const anschalten = !vorher.has(key);
          for (let i = a; i <= b; i += 1) {
            if (anschalten) naechste.add(reihenfolge[i]); else naechste.delete(reihenfolge[i]);
          }
          return naechste;
        }
      }
      if (naechste.has(key)) naechste.delete(key); else naechste.add(key);
      return naechste;
    });
    anchorRef.current = key;
  };

  const toggleAlleSichtbaren = () => {
    setPicked((vorher) => {
      const naechste = new Set(vorher);
      if (shownKeys.every((key) => naechste.has(key))) shownKeys.forEach((key) => naechste.delete(key));
      else shownKeys.forEach((key) => naechste.add(key));
      return naechste;
    });
    anchorRef.current = null;
  };

  const leereAuswahl = () => {
    setPicked(new Set());
    anchorRef.current = null;
  };

  // Namentliche Beschreibung eines abgelehnten Eintrags — eine reine ID sagt dem Support nichts.
  const beschreibe = (eintrag) => {
    const treffer = items.find((item) => item.kind === eintrag.kind && String(item.id) === String(eintrag.id));
    const name = (treffer && (treffer.who || treffer.title)) || eintrag.id;
    return `${name} — ${BULK_REASON[eintrag.reason] || eintrag.reason || 'unbekannter Grund'}`;
  };

  // Immer zuerst dry_run: die Zahlen in der Rückfrage stammen aus der SERVERANTWORT,
  // nie aus einer eigenen Hochrechnung.
  const runBulk = (action) => {
    const auswahl = [...picked].map(zerlegeSchluessel);
    if (!auswahl.length || bulkBusy) return;
    setBulkBusy(true);
    api.post(bulkRoute(action), { action, items: auswahl, dry_run: true })
      .then((response) => setRueckfrage({ action, auswahl, vorschau: response.data || {} }))
      .catch((e) => toast.error(e.response?.data?.error || 'Vorschau nicht möglich.'))
      .finally(() => setBulkBusy(false));
  };

  const fuehreAus = () => {
    if (!rueckfrage || bulkBusy) return;
    const { action, auswahl } = rueckfrage;
    setRueckfrage(null);
    setBulkBusy(true);
    api.post(bulkRoute(action), { action, items: auswahl, dry_run: false })
      .then((response) => {
        const daten = response.data || {};
        const abgelehnt = [...(daten.unchanged || []), ...(daten.refused || [])];
        // Bleibende Notiz über der Liste — kein verschwindender Hinweis.
        setBulkNote({
          action,
          gesamt: auswahl.length,
          erledigt: (daten.counts && daten.counts.done) ?? (daten.done || []).length,
          zeilen: abgelehnt.map(beschreibe),
        });
        setSelected(null);
        leereAuswahl();
        load();
      })
      .catch((e) => toast.error(e.response?.data?.error || 'Sammelaktion fehlgeschlagen.'))
      .finally(() => setBulkBusy(false));
  };

  return (
    <div className="support-page">
      <PageHeader
        title="Support"
        description="Systemweiter Posteingang für Tickets, Fundsachen und Live-Chats."
        actions={
        <div className="support-heading-actions">
          <button type="button" onClick={() => setStartingConversation(true)}>
            <MessageSquare size={14} /> Gespräch starten
          </button>
          <span><Clock3 size={13} /> 30 Sek. Live-Abgleich</span>
          {counts.unread > 0 && <strong>{counts.unread} ungelesen</strong>}
          <button type="button" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> {loading ? 'Lädt…' : 'Aktualisieren'}
          </button>
        </div>
      }
      />

      <SupportStartConversation open={startingConversation} onClose={() => setStartingConversation(false)}
        onStarted={(conversation) => {
          setStartingConversation(false); setView('aktiv'); setFilter(''); setQuery('');
          setSelected(conversation); leereAuswahl();
          if (view === 'aktiv') load();
          toast.success('Supportgespräch gestartet.');
        }} />

      <section className="support-channel-strip">
        <div>
          <span className="support-channel-icon"><Inbox size={18} /></span>
          <div><strong>Ein Posteingang, drei Kanäle</strong><small>Jeder Vorgang behält seinen bestehenden Daten- und Aktionsweg.</small></div>
        </div>
        <div className="support-channel-counts">
          <span><Mail size={13} /> Tickets <b>{counts.ticket ?? 0}</b></span>
          <span><Package size={13} /> Fundsachen <b>{counts.fundsache ?? 0}</b></span>
          <span><MessageSquare size={13} /> Chats <b>{counts.chat ?? 0}</b></span>
        </div>
      </section>

      {error && <div className="support-error" role="alert"><AlertTriangle size={14} /> {error}</div>}

      <section className="support-toolbar">
        <div className="support-view-switch" aria-label="Posteingangsansicht">
          {[
            { key: 'aktiv', label: 'Aktiv', count: counts.aktiv, icon: Inbox },
            { key: 'archiv', label: 'Archiv', count: counts.archiv, icon: Archive },
            // F4: Papierkorb. Entfernen ist ausblenden, nicht löschen — hier steht, was ausgeblendet ist.
            { key: 'entfernt', label: 'Entfernt', count: counts.entfernt, icon: Trash2 },
          ].map((option) => {
            const Icon = option.icon;
            return (
              <button
                type="button"
                key={option.key}
                className={view === option.key ? 'is-active' : ''}
                aria-pressed={view === option.key}
                onClick={() => {
                  setView(option.key);
                  setSelected(null);
                  // Ansichtswechsel leert die Auswahl: ein Stapel aus zwei Ansichten wäre nie eindeutig.
                  leereAuswahl();
                  setBulkNote(null);
                }}
              >
                <Icon size={13} /> {option.label} <b>{option.count ?? 0}</b>
              </button>
            );
          })}
        </div>

        <div className="support-type-filters" aria-label="Supportkanal">
          {FILTERS.map((option) => (
            <button
              type="button"
              key={option.key}
              className={filter === option.key ? 'is-active' : ''}
              aria-pressed={filter === option.key}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
              <b>{option.key ? (counts[option.key] ?? 0) : (counts.alle ?? 0)}</b>
            </button>
          ))}
        </div>

        <label className="support-search">
          <Search size={14} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, Betreff oder Inhalt suchen" />
        </label>
      </section>

      {/* F3: Der Balken wird NUR bei nicht leerer Auswahl gerendert. Das ist keine Kosmetik —
          die Listenzeilen sind Knöpfe, und ein dauerhaft sichtbarer Balken über der Liste
          würde Klicks abfangen, die den Vorgang öffnen sollen. Bitte nicht „vereinfachen". */}
      {picked.size > 0 && (
        <section className="support-bulkbar" data-testid="support-bulkbar" aria-label="Sammelaktion">
          <strong>{picked.size} ausgewählt</strong>
          {versteckteAuswahl > 0 && <small>{versteckteAuswahl} davon aktuell ausgeblendet</small>}
          <div className="support-bulkbar-actions">
            <button
              type="button"
              data-testid="support-bulk-archivieren"
              onClick={() => runBulk('archivieren')}
              disabled={bulkBusy || !bietetAn('archivieren')}
            >
              <Archive size={13} /> Ausgewählte archivieren
            </button>
            {view !== 'entfernt' && (
              <button
                type="button"
                data-testid="support-bulk-wieder-oeffnen"
                onClick={() => runBulk('wieder_oeffnen')}
                disabled={bulkBusy || !bietetAn('wieder_oeffnen')}
              >
                <Undo2 size={13} /> Ausgewählte zurückholen
              </button>
            )}
            {view === 'entfernt' && (
              <button
                type="button"
                data-testid="support-bulk-wiederherstellen"
                onClick={() => runBulk('wiederherstellen')}
                disabled={bulkBusy || !bietetAn('wiederherstellen')}
              >
                <Undo2 size={13} /> Ausgewählte zurückholen
              </button>
            )}
            {view !== 'entfernt' && (
              <button
                type="button"
                className="is-danger"
                data-testid="support-bulk-entfernen"
                onClick={() => runBulk('entfernen')}
                disabled={bulkBusy || !bietetAn('entfernen')}
              >
                <Trash2 size={13} /> Ausgewählte entfernen
              </button>
            )}
            <button type="button" data-testid="support-bulk-clear" onClick={leereAuswahl}>Auswahl aufheben</button>
          </div>
        </section>
      )}

      <section className="support-workspace">
        <aside className="support-list-panel">
          <header>
            <div>
              <span className="support-overline">{view === 'entfernt' ? 'Aus dem Postfach ausgeblendet' : view === 'archiv' ? 'Abgeschlossene Vorgänge' : 'Aktuelle Queue'}</span>
              <h2>{view === 'entfernt' ? 'Entfernt' : view === 'archiv' ? 'Archiv' : 'Offene Vorgänge'}</h2>
            </div>
            <strong>{shown.length}</strong>
          </header>
          {shown.length > 0 && (
            <div className="support-list-tools">
              <label>
                <input
                  type="checkbox"
                  data-testid="support-select-all"
                  aria-label="Alle sichtbaren Vorgänge auswählen"
                  checked={alleSichtbarGewaehlt}
                  onChange={toggleAlleSichtbaren}
                />
                {/* „Alle" meint ausdrücklich die gefilterte, geladene Liste — nicht den Bestand. */}
                <span>Alle {shown.length} sichtbaren auswählen</span>
              </label>
              {truncated && <small>Liste zeigt höchstens 300 Vorgänge</small>}
            </div>
          )}
          {bulkNote && (
            <div className="support-list-note" role="status">
              <div>
                <strong>
                  {BULK_TITEL[bulkNote.action] ? BULK_TITEL[bulkNote.action].replace('?', '') : bulkNote.action}: {bulkNote.erledigt} von {bulkNote.gesamt} ausgeführt
                  {bulkNote.zeilen.length > 0 ? `, ${bulkNote.zeilen.length} nicht möglich` : ''}.
                </strong>
                <button type="button" onClick={() => setBulkNote(null)}>Notiz schließen</button>
              </div>
              {bulkNote.zeilen.length > 0 && (
                <ul>{bulkNote.zeilen.map((zeile, index) => <li key={index}>{zeile}</li>)}</ul>
              )}
            </div>
          )}
          <div className="support-list">
            {shown.length === 0 && (
              <div className="support-empty">
                <CheckCircle2 size={22} />
                <strong>{view === 'archiv' ? 'Archiv ist leer' : 'Keine passenden Vorgänge'}</strong>
                <small>Filter oder Suche anpassen, um weitere Einträge zu sehen.</small>
              </div>
            )}
            {shown.map((item) => {
              const active = selected && selected.kind === item.kind && selected.id === item.id;
              const key = `${item.kind}:${item.id}`;
              const gewaehlt = picked.has(key);
              // Die Zustandsklassen hängen an BEIDEN: der Wrapper trägt Rahmen und Kante,
              // der Knopf bleibt das Element mit der data-testid, an dem sie gelesen werden.
              const zustand = `${active ? 'is-selected' : ''} ${item.unread ? 'is-unread' : ''} ${item.priority === 'urgent' ? 'is-urgent' : ''}`;
              return (
                <div key={key} className={`support-case-row ${zustand} ${gewaehlt ? 'is-picked' : ''}`}>
                  <label className="support-case-pick">
                    <input
                      type="checkbox"
                      aria-label="Vorgang auswählen"
                      data-testid={`support-select-${item.kind}-${item.id}`}
                      checked={gewaehlt}
                      onClick={(event) => { shiftRef.current = event.shiftKey; event.stopPropagation(); }}
                      onChange={() => { const shift = shiftRef.current; shiftRef.current = false; togglePick(item, shift); }}
                    />
                  </label>
                <button
                  type="button"
                  className={`support-case ${zustand}`}
                  data-testid={`support-case-${item.kind}-${item.id}`}
                  onClick={() => setSelected(item)}
                >
                  <div className="support-case-top">
                    <KindPill kind={item.kind} />
                    <span className="support-case-time">{fmt(item.at)}</span>
                  </div>
                  <strong>{item.who || item.title}</strong>
                  <span>{item.title}</span>
                  {item.snippet && <small>{item.snippet}</small>}
                  <div className="support-case-foot">
                    <StatusPill value={item.status} />
                    {item.case_id && <PriorityPill priority={item.priority} category={item.category} />}
                    {item.unread && <b>Neu</b>}
                  </div>
                </button>
                </div>
              );
            })}
          </div>
        </aside>

        <article className="support-detail-panel">
          {!selected ? (
            <div className="support-detail-placeholder">
              <span><UserRound size={21} /></span>
              <strong>Vorgang auswählen</strong>
              <small>Verlauf, Kontakt, Status und die jeweils zulässigen Supportaktionen erscheinen hier.</small>
            </div>
          ) : selected.kind === 'ticket' ? (
            <TicketDetail id={selected.id} archived={view === 'archiv'} onChanged={load} onArchive={afterArchive} />
          ) : selected.kind === 'fundsache' ? (
            <FundsacheDetail id={selected.id} archived={view === 'archiv'} onChanged={load} onArchive={afterArchive} />
          ) : (
            <ChatDetail id={selected.id} archived={view === 'archiv'} onChanged={load} onArchive={afterArchive} />
          )}
        </article>
      </section>

      {/* F3: Die Zahlen stammen ausschließlich aus der dry_run-Antwort des Servers.
          Erst nach „Jetzt ausführen" läuft derselbe Aufruf mit dry_run:false. */}
      <ConfirmModal
        open={Boolean(rueckfrage)}
        title={rueckfrage ? (BULK_TITEL[rueckfrage.action] || 'Sammelaktion ausführen?') : ''}
        body={rueckfrage ? [
          `${rueckfrage.auswahl.length} Vorgänge geprüft.`,
          `${(rueckfrage.vorschau.counts && rueckfrage.vorschau.counts.done) ?? 0} werden ausgeführt.`,
          `${(rueckfrage.vorschau.counts && rueckfrage.vorschau.counts.unchanged) ?? 0} ohne Wirkung, `
            + `${(rueckfrage.vorschau.counts && rueckfrage.vorschau.counts.refused) ?? 0} nicht möglich.`,
          rueckfrage.action === 'entfernen'
            ? 'Entfernen blendet nur aus dem Admin-Postfach aus. Es wird nichts gelöscht und der Schritt ist umkehrbar.'
            : '',
        ].filter(Boolean).join('\n') : ''}
        confirmLabel="Jetzt ausführen"
        danger={Boolean(rueckfrage && rueckfrage.action === 'entfernen')}
        onConfirm={fuehreAus}
        onCancel={() => setRueckfrage(null)}
      />
    </div>
  );
}
